import React from 'react'
import { Confirm, FlexHorizontal, LoadMask, Spacer, useToasts } from '@vestaboard/installables'
import { ColorTemplateForm } from '../components/ColorTemplateForm'
import { CircularProgress, Typography } from '@material-ui/core'
import { useHistory, useParams } from 'react-router'
import { useUpdateColorTemplate, useDeleteTemplateMessage } from '../hooks/useUpdateColorTemplate'
import { useCreateTemplateMessage } from '../hooks/useCreateColorTemplate'
import { useGetColorTemplate } from '../hooks/useGetColorTemplate'
import { Alert } from '@material-ui/lab'
import { useDeleteColorTemplate } from '../hooks/useDeleteColorTemplate'

export const UpdateColorTemplate = () => {
  const params = useParams<{ id: string }>()
  const { loading, data, error } = useGetColorTemplate(params.id)
  const [updateColorTemplate, { loading: saving }] = useUpdateColorTemplate()
  const [deleteColorTemplate, { loading: deleting }] = useDeleteColorTemplate()
  const [createTemplateMessage] = useCreateTemplateMessage()
  const [deleteTemplateMessage] = useDeleteTemplateMessage()
  const [confirmDelete, setConfirmDelete] = React.useState(false)
  const { addToast } = useToasts()
  const history = useHistory()

  return (
    <>
      {saving || deleting ? <LoadMask /> : null}
      <Confirm
        title='Delete Color Template? This cannot be undone!'
        handleClose={() => {
          setConfirmDelete(false)
        }}
        open={confirmDelete}
        handleAccept={async () => {
          const response = await deleteColorTemplate({
            variables: {
              input: {
                id: params.id
              }
            }
          })

          if (response.data?.deleteColorTemplate?.__typename === 'TemplateCategoryV2') {
            history.push(`/color-templates`)
          } else {
            addToast(response?.data?.deleteColorTemplate?.error || 'There was an error updating your color template', {
              appearance: 'error',
              autoDismiss: true
            })
          }
        }}></Confirm>
      <FlexHorizontal spaceBetween>
        <Typography variant='h4'>Update Color Template</Typography>
      </FlexHorizontal>
      <Spacer size='large' />
      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity='error'>There was an error loading this color template</Alert>
      ) : data?.colorTemplate?.__typename !== 'TemplateCategoryV2' ? (
        <Alert severity='error'>{data?.colorTemplate.error || '!!!'}</Alert>
      ) : (
        <ColorTemplateForm
          colorTemplate={{
            title: data.colorTemplate.title,
            messages: (data.templateMessages?.templateMessages || []).map(tm => ({
              id: tm.message.id,
              text: tm.message.text,
              characters: tm.message.characters
            }))
          }}
          onDelete={() => {
            setConfirmDelete(true)
          }}
          onSave={async input => {
            const response = await updateColorTemplate({
              variables: {
                input: {
                  id: params.id,
                  title: input.title
                }
              }
            })

            if (response.data?.updateColorTemplate?.__typename === 'TemplateCategoryV2') {
              const newMessageIds = (input.messages || []).map(m => m.id)
              const currentTemplateMessages = data.templateMessages?.templateMessages || []
              const currentMessageIds = currentTemplateMessages.map(tm => tm.message.id)

              const toAdd = newMessageIds.filter(id => !currentMessageIds.includes(id))
              const toRemove = currentTemplateMessages.filter(tm => !newMessageIds.includes(tm.message.id))

              const mutationResults = await Promise.all([
                ...toAdd.map(messageId =>
                  createTemplateMessage({
                    variables: { input: { categoryId: params.id, messageId } }
                  })
                ),
                ...toRemove.map(tm =>
                  deleteTemplateMessage({
                    variables: { input: { id: tm.id } }
                  })
                )
              ])

              const failed = mutationResults.filter(r => {
                const result = (r.data as any)?.createTemplateMessageV2 ?? (r.data as any)?.deleteTemplateMessageV2
                return result?.__typename !== 'TemplateMessageV2'
              })

              if (failed.length > 0) {
                addToast(`Color template saved but ${failed.length} message operation(s) failed`, {
                  appearance: 'error',
                  autoDismiss: true
                })
              } else {
                history.push(`/color-templates`)
              }
            } else {
              addToast(
                response?.data?.updateColorTemplate?.error || 'There was an error updating your color template',
                {
                  appearance: 'error',
                  autoDismiss: true
                }
              )
            }
          }}
        />
      )}
    </>
  )
}
